import type { Integration, IntegrationOAuthSecret } from "@prescreen/lib/types"
import {
  integrationHasProvider,
  resolveIntegrationProviderIds,
} from "@prescreen/lib/integrations/integration-providers"
import {
  BANKING_SCOPE,
  bankingProductScopesFromCapabilities,
  DEFAULT_BANKING_CAPABILITIES,
  deriveBankingCapabilitiesFromGrantedScopes,
  secretGrantsIncludeMinimumReadableAccess,
  type BankingCapabilities,
  type BankingPermissionTier,
} from "@prescreen/lib/types"

export function integrationHasJackHenrySilverlake(integration: Integration): boolean {
  return integrationHasProvider(integration, "jack_henry_silverlake")
}

export function integrationHasJackHenrySynergy(integration: Integration): boolean {
  return integrationHasProvider(integration, "jack_henry_synergy")
}

export function integrationHasEquifax(integration: Integration): boolean {
  return (
    integrationHasProvider(integration, "equifax_prequal") ||
    integrationHasProvider(integration, "equifax_dit")
  )
}

export function integrationHasConfigurableOAuthSecrets(integration: Integration): boolean {
  return (
    integrationHasJackHenrySilverlake(integration) ||
    integrationHasJackHenrySynergy(integration) ||
    integrationHasEquifax(integration)
  )
}

export { resolveIntegrationProviderIds, integrationHasProvider }

function addReadWriteTier(
  set: Set<string>,
  tier: BankingPermissionTier,
  readScope: string,
  writeScope: string
): void {
  if (tier === "read") {
    set.add(readScope)
    return
  }
  if (tier === "write") {
    set.add(readScope)
    set.add(writeScope)
  }
}

export function buildSecretGrantedScopesFromSelections(
  integration: Integration,
  bankingCaps: BankingCapabilities,
  synergyDocumentsTier: BankingPermissionTier,
  prequalTier: BankingPermissionTier,
  hardpullTier: BankingPermissionTier
): string[] {
  const set = new Set<string>()
  const hasSilverlake = integrationHasJackHenrySilverlake(integration)
  const hasSynergy = integrationHasJackHenrySynergy(integration)
  const hasPrequal = integrationHasProvider(integration, "equifax_prequal")
  const hasDit = integrationHasProvider(integration, "equifax_dit")

  if (hasSilverlake) {
    for (const s of bankingProductScopesFromCapabilities(bankingCaps)) {
      set.add(s)
    }
  }
  if (hasSynergy) {
    addReadWriteTier(
      set,
      synergyDocumentsTier,
      BANKING_SCOPE.DOCUMENTS_READ,
      BANKING_SCOPE.DOCUMENTS_WRITE
    )
  }
  if (hasPrequal) {
    addReadWriteTier(set, prequalTier, BANKING_SCOPE.PREQUAL_READ, BANKING_SCOPE.PREQUAL_WRITE)
  }
  if (hasDit) {
    addReadWriteTier(set, hardpullTier, BANKING_SCOPE.HARDPULL_READ, BANKING_SCOPE.HARDPULL_WRITE)
  }

  return Array.from(set)
}

export function secretFormSelectionsMeetMinimum(
  integration: Integration,
  bankingCaps: BankingCapabilities,
  synergyDocumentsTier: BankingPermissionTier,
  prequalTier: BankingPermissionTier,
  hardpullTier: BankingPermissionTier
): boolean {
  const scopes = buildSecretGrantedScopesFromSelections(
    integration,
    bankingCaps,
    synergyDocumentsTier,
    prequalTier,
    hardpullTier
  )
  return secretGrantsIncludeMinimumReadableAccess(scopes)
}

function deriveSynergyDocumentsTierFromGrantedScopes(
  scopes: readonly string[]
): BankingPermissionTier {
  const s = new Set(scopes)
  if (s.has(BANKING_SCOPE.DOCUMENTS_WRITE)) {
    return "write"
  }
  if (s.has(BANKING_SCOPE.DOCUMENTS_READ)) {
    return "read"
  }
  return "none"
}

function deriveReadWriteTier(
  scopes: ReadonlySet<string>,
  readScope: string,
  writeScope: string
): BankingPermissionTier {
  if (scopes.has(writeScope)) {
    return "write"
  }
  if (scopes.has(readScope)) {
    return "read"
  }
  return "none"
}

export function deriveSecretFormStateFromGrantedScopes(grantedScopes: readonly string[]): {
  capabilities: BankingCapabilities
  synergyDocumentsTier: BankingPermissionTier
  prequalTier: BankingPermissionTier
  hardpullTier: BankingPermissionTier
} {
  const scopeSet = new Set(grantedScopes)
  return {
    capabilities: deriveBankingCapabilitiesFromGrantedScopes(grantedScopes),
    synergyDocumentsTier: deriveSynergyDocumentsTierFromGrantedScopes(grantedScopes),
    prequalTier: deriveReadWriteTier(scopeSet, BANKING_SCOPE.PREQUAL_READ, BANKING_SCOPE.PREQUAL_WRITE),
    hardpullTier: deriveReadWriteTier(
      scopeSet,
      BANKING_SCOPE.HARDPULL_READ,
      BANKING_SCOPE.HARDPULL_WRITE
    ),
  }
}

/** Space-separated string for the OAuth 2.0 token request `scope` parameter (per RFC 6749). */
export function oauthTokenScopeParameterFromSecret(
  secret: Pick<IntegrationOAuthSecret, "grantedScopes">
): string {
  return Array.from(
    new Set(secret.grantedScopes.map(s => s.trim()).filter(Boolean))
  ).join(" ")
}

export { DEFAULT_BANKING_CAPABILITIES }
